import { entriesIterator } from '@sgme/fp';
import { smartQuoteApiConnector } from 'apiConnector/smart-quote';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useUserNotification } from 'hooks/useUserNotification';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { lastValueFrom } from 'rxjs';
import { smartQuoteActions } from 'state/workspaces/smart-quote/actions';
import { httpClient } from 'utils/http/httpClient';
import { extractHttpErrorMessage, HttpClientError } from 'utils/http/httpClient.model';
import { scrollPageToBottom } from 'utils/scroll';

export type PromptData = { email: File; client?: ISalesCounterpart };

const clientFormDataPrefix = 'json.selectedOnBehalfClient';
const clientKeyMapping = {
  id: 'bdrLegalId',
  label: 'bdrName',
  eliotCode: 'eliot',
  countryResidenceCode: 'countryResidenceCode',
} as const;

const appendClient = (formData: FormData, client: ISalesCounterpart) => {
  for (const [clientKey, formDataKeySuffix] of entriesIterator(clientKeyMapping)) {
    if (client[clientKey]) {
      formData.append(`${clientFormDataPrefix}.${formDataKeySuffix}`, client[clientKey]);
    }
  }
  return formData;
};

export function useSendPrompt(pageRef: RefObject<HTMLDivElement | null>) {
  const dispatch = useDispatch();
  const tabId = useAssertedActiveTabId();
  const { error } = useUserNotification();

  const handlePromptSubmit = async ({ email, client }: PromptData) => {
    const formData = new FormData();
    formData.append('file', email);

    if (client) {
      appendClient(formData, client);
    }

    try {
      const parseEmailResponse = await lastValueFrom(smartQuoteApiConnector(httpClient).parseEmail(tabId, formData));
      dispatch(smartQuoteActions.parseEmailSuccess(tabId, email.name, parseEmailResponse, client));
      scrollPageToBottom(pageRef);
    } catch (err) {
      error({
        title: extractHttpErrorMessage(err as HttpClientError) ?? {
          id: 'generic.error.message',
        },
      });
    }
  };

  return { handlePromptSubmit };
}
